import React, { useId } from 'react';

/**
 * La marca de Maqaio, en sus dos versiones.
 *
 * El original venía en un lienzo de 450x90 con el contenido real ocupando
 * sólo 4..331 en horizontal y 14..78 en vertical -- medido en Chromium, no a
 * ojo. Un cuarto del ancho era aire, así que a cualquier alto fijo la marca
 * salía diminuta y descentrada. Aquí el viewBox va ajustado al dibujo, de
 * modo que `height` significa de verdad el alto de la marca y el ancho sale
 * solo del ratio.
 *
 * Dos versiones y no una escalada, porque el bloque completo tiene un ratio
 * de casi 4:1: a un alto que quepa en la cabecera del móvil, la palabra
 * "maqaio" se quedaría en 7px y el subtexto sería una mancha gris. En móvil
 * va sólo el isotipo, que es la pieza que aguanta a tamaño pequeño -- igual
 * que hacía el cuadrado naranja al que sustituye.
 *
 * OJO con las fuentes: el wordmark es texto, no trazados, y la aplicación no
 * carga ninguna fuente web. 'Arial Black' existe en Windows y macOS, pero en
 * Android y en la mayoría de Linux no, y ahí cae en la genérica sans-serif y
 * la marca cambia de forma. El isotipo no tiene ese problema (es geometría
 * pura), que es otra razón para que sea él quien va en móvil.
 */

/** Colores propios de la marca, no del tema: son del logotipo, no de la interfaz. */
const INK = '#181B20';
const FIRE_FROM = '#FF3B00';
const FIRE_TO = '#FF8700';
const SUBTEXT = '#717D8A';

interface MaqaioLogoProps {
  /** Alto en píxeles. El ancho lo pone el ratio del dibujo. */
  height?: number;
  /** Texto para lectores de pantalla. Vacío = decorativo (aria-hidden). */
  title?: string;
}

/** `useId` mete dos puntos en el identificador y `url(#...)` no los traga. */
const useGradientId = (): string => `fire-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

const FireGradient: React.FC<{ id: string }> = ({ id }) => (
  <defs>
    <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stopColor={FIRE_FROM} />
      <stop offset="100%" stopColor={FIRE_TO} />
    </linearGradient>
  </defs>
);

/**
 * Sólo el isotipo: las dos cuñas y el punto blanco. Ratio 1.46:1, geometría
 * pura (sin texto), así que se ve igual en cualquier dispositivo y aguanta
 * tamaños pequeños. Es el que va en la cabecera del móvil.
 */
export const MaqaioMark: React.FC<MaqaioLogoProps> = ({ height = 34, title }) => {
  const gradientId = useGradientId();
  return (
    <svg
      // Ajustado al dibujo medido (x 8.92..62.84, y 23..60) con una unidad de
      // aire alrededor, para que el trazo no toque el borde al redondear.
      viewBox="7.9 22 56 39"
      height={height}
      width={(height * 56) / 39}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <FireGradient id={gradientId} />
      <g transform="skewX(-15) translate(25, 18)">
        <polygon points="0,42 12,5 32,5 20,42" fill={INK} />
        <polygon points="22,42 34,5 44,5 32,42" fill={`url(#${gradientId})`} />
        <circle cx="36" cy="12" r="3.5" fill="#FFFFFF" />
      </g>
    </svg>
  );
};

/**
 * El bloque completo: isotipo + "maqaio" + subrayado + descriptor. Ratio
 * ~3.9:1, para la cabecera de escritorio.
 *
 * El subrayado del original llegaba a x=318.9 cuando el elemento más largo
 * que tiene debajo (el descriptor) acaba en 264.3: sobresalían 55 unidades
 * colgando en el vacío. Aquí termina justo después del descriptor.
 */
export const MaqaioLockup: React.FC<MaqaioLogoProps> = ({ height = 50, title }) => {
  const gradientId = useGradientId();
  return (
    <svg
      // Ajustado al contenido medido (x 4.11..266, y 14.21..77.97).
      viewBox="2 12 266 68"
      height={height}
      width={(height * 266) / 68}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      // overflow visible porque el ancho del wordmark depende de la fuente
      // que tenga el dispositivo (ver la nota de arriba). Medido aqui sobra
      // sitio, pero con 'Arial Black' de verdad el texto crece, y es mejor
      // que se salga un pelo del lienzo a que salga cortado por la mitad.
      style={{ display: 'block', flexShrink: 0, overflow: 'visible' }}
    >
      <FireGradient id={gradientId} />
      <g transform="skewX(-15) translate(25, 12)">
        <g transform="translate(0, 6)">
          <polygon points="0,42 12,5 32,5 20,42" fill={INK} />
          <polygon points="22,42 34,5 44,5 32,42" fill={`url(#${gradientId})`} />
          <circle cx="36" cy="12" r="3.5" fill="#FFFFFF" />
        </g>

        <text
          x="55"
          y="42"
          fontFamily="'Arial Black', 'Montserrat', sans-serif"
          fontWeight="900"
          fontSize="44"
          fontStyle="italic"
          letterSpacing="-1.5"
        >
          <tspan fill={INK}>maq</tspan>
          <tspan fill={`url(#${gradientId})`}>ai</tspan>
          <tspan fill={INK}>o</tspan>
        </text>

        {/* 257/247 en vez de 310/300: así acaba a la altura del descriptor. */}
        <polygon points="56,48 257,48 247,52 46,52" fill={`url(#${gradientId})`} />

        <text
          x="58"
          y="64"
          fontFamily="'Inter', sans-serif"
          fontWeight="800"
          fontSize="9"
          fontStyle="italic"
          fill={SUBTEXT}
          letterSpacing="2"
        >
          HEAVY EQUIPMENT OPERATIONS
        </text>
      </g>
    </svg>
  );
};
